"use client"

/**
 * Apply motion: the thinking wash sits on the canvas field that will change.
 * Ask Leo stays still. Finding chips keep the same motion API.
 */

import * as React from "react"

import { LeoThinkingBackdrop } from "@/components/leo-thinking-backdrop"
import { useQuestionComposerContextOrNull } from "@/components/question-composer/composer-context"
import { LEO_DEEP_FILL_CLASS, LeoIcon } from "@/components/ui/leo-icon"
import {
  LEO_APPLY_ATTR,
  LEO_APPLY_RECEIVING_ATTR,
  leoApplyFieldIsReceiving,
  type LeoApplyAnchor,
  type LeoApplyGhosts,
} from "@/lib/question-quality/leo-apply-targets"
import { cn } from "@/lib/utils"

export type LeoApplyCtaMotion = "working" | "answered"

export type LeoPhase =
  | "idle"
  | "acknowledge"
  | "handoff"
  | "compose"
  | "apply"
  | "resolve"

type LeoApplyMotionApi = {
  phase: LeoPhase
  captureSource: (el: HTMLElement, key: string) => void
  armApplyKey: (key: string, onSettled?: () => void) => void
  beginHandoff: () => void
  beginCompose: () => void
  endHandoff: () => void
  resetMotion: () => void
  playApply: (
    anchors: LeoApplyAnchor[],
    ghosts?: LeoApplyGhosts,
    nextTexts?: LeoApplyGhosts,
  ) => void
  applyingKey: string | null
  applyingMotion: LeoApplyCtaMotion | null
  receive: null
}

export const LEO_ACKNOWLEDGE_MS = 280
export const LEO_APPLY_ANSWERED_MS = 480

function leoApplyAnsweredDelayMs(_reduceMotion = false): number {
  return 0
}

const noopApi: LeoApplyMotionApi = {
  phase: "idle",
  captureSource: () => {},
  armApplyKey: () => {},
  beginHandoff: () => {},
  beginCompose: () => {},
  endHandoff: () => {},
  resetMotion: () => {},
  playApply: () => {},
  applyingKey: null,
  applyingMotion: null,
  receive: null,
}

const LeoApplyMotionContext = React.createContext<LeoApplyMotionApi>(noopApi)

export function LeoApplyMotionProvider({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <LeoApplyMotionContext.Provider value={noopApi}>
      {children}
    </LeoApplyMotionContext.Provider>
  )
}

export function useLeoApplyMotion(): LeoApplyMotionApi {
  return React.useContext(LeoApplyMotionContext)
}

export function LeoApplyField({
  anchor,
  children,
  className,
  revealBlobs = false,
}: {
  anchor?: LeoApplyAnchor
  children: React.ReactNode
  className?: string
  /** Classification chips: keep the control readable and let the blob wash show. */
  revealBlobs?: boolean
}) {
  const composer = useQuestionComposerContextOrNull()
  const receiving = composer?.leoReceivingAnchors ?? []
  const active =
    Boolean(composer?.leoWorking) && leoApplyFieldIsReceiving(anchor, receiving)

  return (
    <div
      className={cn(
        "relative isolate",
        revealBlobs && "overflow-hidden rounded-md",
        className,
      )}
      {...(anchor ? { [LEO_APPLY_ATTR]: anchor } : {})}
      {...(active ? { [LEO_APPLY_RECEIVING_ATTR]: "" } : {})}
    >
      {active ? <LeoThinkingBackdrop thinking intensity="high" /> : null}
      {active && !revealBlobs ? (
        <div
          className="pointer-events-none absolute inset-0 z-[1] bg-background/65"
          aria-hidden
        />
      ) : null}
      <div className="relative z-[2]">{children}</div>
      {active ? (
        <span
          className={cn(
            "pointer-events-none absolute right-2 top-2 z-[3] inline-flex size-6 items-center justify-center",
            LEO_DEEP_FILL_CLASS,
          )}
          aria-hidden
        >
          <LeoIcon variant="ambient" size="xs" state="working" className="size-4" />
        </span>
      ) : null}
    </div>
  )
}
